/* Research-run endpoints. Response handling mirrors the old call sites
 * (service/review/index.html): the run picker (~3593-3629), startRun
 * (~3535-3591), getRunStatus's poll body (~3554-3555). The modal/poll UI
 * itself is F2b/F2d territory — this module is just the fetch layer they
 * will call into.
 */
import { request } from './client.ts';
import type { ResearchProfileData } from './profiles.ts';
import type { SystemPlanesMap } from '../derive/planes.ts';

export interface RunListEntry {
  file: string;
  name: string;
  size: number;
}

export interface RunListResponse {
  local: RunListEntry[];
  stored: unknown[];
}

/** GET /runs — every archived run on the server (~3598). */
export function listRuns(): Promise<RunListResponse> {
  return request<RunListResponse>('/runs');
}

export interface StartRunPayload {
  brand: string;
  aliases: string[];
  /* OPTIONAL (design F2): omitted from the wire body when the run modal's
   * Scope select is on "Client profile default" — the backend then applies the
   * profile's default_focus (service/profiles.py::effective_run_params). An
   * explicit value ("presence"/"full") always wins. JSON.stringify drops it
   * when undefined, so the profile-default request carries no `focus` key. */
  focus?: string;
  markets: string[];
  /* OPTIONAL (H2): the cross-reference planes to fold in, a subset of
   * enrich.PLANES ("linkgraph"/"impact"/"aiq"/"peers"). The run modal derives
   * this from its plane toggles; an unknown plane 422s server-side. Omitted
   * from the wire body when ALL four are selected (JSON.stringify drops
   * undefined), so a full-coverage run matches the pre-H2 (opt-out default)
   * body byte-for-byte. */
  include?: string[];
  /* Per-run tailoring override — a PARTIAL ResearchProfile dict deep-merged
   * over the client's stored profile server-side (validated → 422). Omitted
   * from the wire body when undefined (JSON.stringify drops it), so the
   * no-override request matches the pre-profiles body byte-for-byte. */
  profile_overrides?: ResearchProfileData;
}

export interface StartRunResponse {
  jobId: string;
  status: string;
}

/** POST /research — kicks off a new run (~3540-3542). The run token goes in
 * the `x-run-token` header, exactly like the old call; a wrong/missing
 * token surfaces as a 401 ApiError, a run already in progress as 409 — the
 * caller (F2b/F2d's run modal) branches on `err.status` the same way the
 * old inline handler did (~3543-3544). */
export function startRun(payload: StartRunPayload, token: string): Promise<StartRunResponse> {
  return request<StartRunResponse>('/research', {
    method: 'POST',
    headers: { 'x-run-token': token },
    body: JSON.stringify(payload),
  });
}

export interface SystemPlanesResponse {
  planes: SystemPlanesMap;
}

/** GET /system/planes — per-plane pre-run availability (H2). Honest dark
 * status with no credentials set (the CI condition): the run modal annotates
 * each plane toggle from this (dark = disabled-off-with-reason; stale AIQ =
 * amber age note). Read-only, no run, no cost. */
export function getSystemPlanes(): Promise<SystemPlanesResponse> {
  return request<SystemPlanesResponse>('/system/planes');
}

/** One streamed feed entry in a run's progress log. */
export interface RunLogEntry {
  t?: number;
  who?: string;
  text?: string;
  [k: string]: unknown;
}

/** GET /research/{jobId} poll body. */
export interface RunStatus {
  status?: string;
  phase?: string | null;
  turns?: number;
  log?: RunLogEntry[];
  logTotal?: number;
  lastNarration?: string | null;
  error?: string | null;
  result?: unknown;
  [k: string]: unknown;
}

/** GET /research/{jobId} — poll body for a run in progress (~3555).
 * `signal` (F4 item 1) threads a poller's AbortSignal through to the
 * underlying fetch (api/client.ts's request() forwards it via RequestInit)
 * so stopping the poller actually cancels an in-flight poll, not just the
 * interval timer. */
export function getRunStatus(jobId: string, signal?: AbortSignal): Promise<RunStatus> {
  return request<RunStatus>(`/research/${jobId}`, { signal });
}
